/**
 * `demo/guide/events.ts` — Help → "Show the guide" asks the guide to open
 * its chooser through `requestDemoGuideChooser()` (#2824). The roster page
 * tells the guide when a solve or a publish is done
 * (`notifyDemoGuideAction`), so a step can wait for the visitor to do it.
 * With no guide mounted the calls do nothing.
 */
const CHOOSER = "tarii:demo-guide-chooser";

export function requestDemoGuideChooser(): void {
  window.dispatchEvent(new CustomEvent(CHOOSER));
}

const ACTION = "tarii:demo-guide-action";

/** Something the visitor did that a guide step can wait for. */
export type DemoGuideAction =
  | "solved"
  | "published"
  | "request-sent"
  | "request-accepted"
  | "swap-confirmed"
  | "phone-opened"
  | "member-added"
  | "change-made"
  /** The "Change the scenario" dialog opened (the roster tour's last step, #2962). */
  | "scenario-opened";

export function notifyDemoGuideAction(action: DemoGuideAction): void {
  window.dispatchEvent(new CustomEvent<DemoGuideAction>(ACTION, { detail: action }));
}

/** Returns the unsubscribe function. */
export function subscribeDemoGuideAction(handler: (action: DemoGuideAction) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<DemoGuideAction>).detail);
  window.addEventListener(ACTION, listener);
  return () => window.removeEventListener(ACTION, listener);
}

/** Returns the unsubscribe function. */
export function subscribeDemoGuideChooser(handler: () => void): () => void {
  window.addEventListener(CHOOSER, handler);
  return () => window.removeEventListener(CHOOSER, handler);
}

const SCENARIO = "tarii:demo-guide-scenario";

/** The visitor picked a scenario: the guide starts its tour. */
export function startDemoGuideScenario(variant: string): void {
  window.dispatchEvent(new CustomEvent<string>(SCENARIO, { detail: variant }));
}

/** Returns the unsubscribe function. */
export function subscribeDemoGuideScenario(handler: (variant: string) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<string>).detail);
  window.addEventListener(SCENARIO, listener);
  return () => window.removeEventListener(SCENARIO, listener);
}

const SCENARIO_PENDING = "tarii:demo-guide-scenario-pending";

/**
 * The visitor pressed "Use this change" and the server is rebuilding the team:
 * the guide drops its old card and folds the phone at once. `pending: false`
 * means the change failed or did not start a scenario.
 */
export function setDemoGuideScenarioPending(pending: boolean): void {
  window.dispatchEvent(new CustomEvent<boolean>(SCENARIO_PENDING, { detail: pending }));
}

/** Returns the unsubscribe function. */
export function subscribeDemoGuideScenarioPending(handler: (pending: boolean) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<boolean>).detail);
  window.addEventListener(SCENARIO_PENDING, listener);
  return () => window.removeEventListener(SCENARIO_PENDING, listener);
}

/** Switch the demo phone to one person (the phone listens for this event). */
export function showDemoPhonePerson(membershipId: string, opts: { open?: boolean } = {}): void {
  window.dispatchEvent(
    new CustomEvent("tarii:demo-phone-person", { detail: { membershipId, open: opts.open } }),
  );
}

const AUTOPLAY = "tarii:demo-guide-autoplay";

/** What Autoplay plays: the tour on screen (a scenario's too), the introduction or this page's tour. */
export type AutoplayTarget = { kind: "current" } | { kind: "intro" } | { kind: "page" };

/** Start Autoplay: the guide plays the chosen flow by itself. */
export function requestDemoGuideAutoplay(target: AutoplayTarget = { kind: "current" }): void {
  window.dispatchEvent(new CustomEvent<AutoplayTarget>(AUTOPLAY, { detail: target }));
}

/** Returns the unsubscribe function. */
export function subscribeDemoGuideAutoplay(handler: (t: AutoplayTarget) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<AutoplayTarget>).detail);
  window.addEventListener(AUTOPLAY, listener);
  return () => window.removeEventListener(AUTOPLAY, listener);
}

const SCENARIO_RESET = "tarii:demo-phone-scenario";

/** The phone goes back to its first screen (a scenario starts). */
export function notifyDemoPhoneScenarioStart(): void {
  window.dispatchEvent(new CustomEvent(SCENARIO_RESET));
}

/** Returns the unsubscribe function. */
export function subscribeDemoPhoneScenarioStart(handler: () => void): () => void {
  window.addEventListener(SCENARIO_RESET, handler);
  return () => window.removeEventListener(SCENARIO_RESET, handler);
}
